import { useAuth } from "@/contexts/AuthHook";

export interface CopiedMatterData {
  type: "PRECEDENT_MATTER_COPY";
  version: "1.0";
  data: Record<string, any>;
  timestamp: number;
  userId: string;
  firmId?: string;
}

// Storage keys
const MATTER_STORAGE_KEY = "precedent_copied_matter";
const STORAGE_VERSION = "1.0";

/**
 * Get storage key with user context to prevent cross-user data access
 */
export const getMatterStorageKey = (
  userId: string,
  firmId?: string,
): string => {
  const firmPart = firmId ? `_firm_${firmId}` : "";
  return `${MATTER_STORAGE_KEY}_user_${userId}${firmPart}`;
};

/**
 * Save copied matter data to localStorage with user context
 */
export const saveCopiedMatter = (
  data: Record<string, any>,
  userId: string,
  firmId?: string,
): void => {
  try {
    const copiedData: CopiedMatterData = {
      type: "PRECEDENT_MATTER_COPY",
      version: STORAGE_VERSION,
      data,
      timestamp: Date.now(),
      userId,
      firmId,
    };

    const storageKey = getMatterStorageKey(userId, firmId);
    localStorage.setItem(storageKey, JSON.stringify(copiedData));
  } catch (error) {
    console.error("Failed to save copied matter to localStorage:", error);
    throw new Error("Unable to save copied matter data");
  }
};

/**
 * Load copied matter data from localStorage with user context validation
 */
export const loadCopiedMatter = (
  userId: string,
  firmId?: string,
): CopiedMatterData | null => {
  try {
    const storageKey = getMatterStorageKey(userId, firmId);
    const storedData = localStorage.getItem(storageKey);

    if (!storedData) {
      return null;
    }

    const copiedData: CopiedMatterData = JSON.parse(storedData);

    // Validate data structure and user context
    if (
      !copiedData ||
      copiedData.type !== "PRECEDENT_MATTER_COPY" ||
      copiedData.version !== STORAGE_VERSION ||
      copiedData.userId !== userId ||
      !copiedData.data ||
      typeof copiedData.data !== "object"
    ) {
      // Invalid data - clean it up
      localStorage.removeItem(storageKey);
      return null;
    }

    // Check if data is expired (optional - set to 24 hours for now)
    const EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours in ms
    if (Date.now() - copiedData.timestamp > EXPIRY_TIME) {
      localStorage.removeItem(storageKey);
      return null;
    }

    return copiedData;
  } catch (error) {
    console.error("Failed to load copied matter from localStorage:", error);
    return null;
  }
};

/**
 * Clear copied matter data for a specific user
 */
export const clearCopiedMatter = (userId: string, firmId?: string): void => {
  try {
    const storageKey = getMatterStorageKey(userId, firmId);
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error("Failed to clear copied matter from localStorage:", error);
  }
};

/**
 * Clear all copied matter data for all users (cleanup utility)
 */
export const clearAllCopiedMatters = (): void => {
  try {
    const keysToRemove: string[] = [];

    // Find all matter storage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(MATTER_STORAGE_KEY)) {
        keysToRemove.push(key);
      }
    }

    // Remove all found keys
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.error(
      "Failed to clear all copied matters from localStorage:",
      error,
    );
  }
};

/**
 * Clean up expired copied matter data across all users
 */
export const cleanupExpiredCopiedMatters = (): void => {
  try {
    const EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours in ms
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(MATTER_STORAGE_KEY)) {
        try {
          const storedData = localStorage.getItem(key);
          if (storedData) {
            const copiedData: CopiedMatterData = JSON.parse(storedData);
            if (Date.now() - copiedData.timestamp > EXPIRY_TIME) {
              keysToRemove.push(key);
            }
          }
        } catch (parseError) {
          // If we can't parse it, it's invalid - remove it
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });

    if (keysToRemove.length > 0) {
      console.log(
        `Cleaned up ${keysToRemove.length} expired copied matter entries`,
      );
    }
  } catch (error) {
    console.error("Failed to cleanup expired copied matters:", error);
  }
};

/**
 * Hook for managing copied matter data with user context
 */
export const useMatterStorage = () => {
  const { userId, firmId } = useAuth();

  const saveMatter = (data: Record<string, any>) => {
    if (!userId) {
      throw new Error("User not authenticated");
    }
    saveCopiedMatter(data, userId, firmId);
  };

  const loadMatter = (): CopiedMatterData | null => {
    if (!userId) {
      return null;
    }
    return loadCopiedMatter(userId, firmId);
  };

  const clearMatter = () => {
    if (!userId) {
      return;
    }
    clearCopiedMatter(userId, firmId);
  };

  return {
    saveMatter,
    loadMatter,
    clearMatter,
    userId,
    firmId,
  };
};
